import type { ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import ModalContext from 'contexts/ModalContext'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Modal from 'LuxKit/Modal/Modal'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import OfferPageBurnPriceDisplay from 'components/OfferPage/Loyalty/OfferPageBurnPriceDisplay'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import useOfferPageBurnPoints from 'hooks/useOfferPageBurnPoints'
import useCruiseCabinDescriptions from 'hooks/Cruise/useCruiseCabinDescriptions'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import type { CruiseDepartureCabinPrice } from 'lib/cruises/cruiseDeparturePricing'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CruisePriceFromLabel from 'components/Cruises/Common/CruisePriceFromLabel'
import CruisePromotionPriceUnit from 'components/Cruises/Common/CruisePromotionPriceUnit'

const PriceItemWrapper = styled(Group)`
  padding-block: ${rem(12)};
`

const CabinPriceCaption = styled(PriceRowPriceCaption)<{ $hideIconOnDesktop: boolean }>`
  ${props => props.$hideIconOnDesktop && `
    ${mediaQueryUp.desktop} {
      button {
        display: none;
      }
    }
  `}
`

interface Props {
  offer: App.CruiseOffer
  price: CruiseDepartureCabinPrice
  hideIconOnDesktop?: boolean
  renderSoldOutContent?: (category: string) => ReactNode
}

/**
 * Surface-neutral content for one cruise cabin category. Consumers own grid
 * layout and may opt into their own sold-out follow-up action.
 */
function CruiseDepartureCabinPriceItem({
  offer,
  price,
  hideIconOnDesktop = false,
  renderSoldOutContent,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const cabinDescriptions = useCruiseCabinDescriptions()
  const loyaltyProductType = useMemo(() => getLoyaltyProductType(offer), [offer])
  const categoryLabel = cabinTypeLabels[price.category] ?? price.category
  const promotionalPrice = price.promotionalPrice
  const perPersonPrice = promotionalPrice?.perPersonPrice
  const hasPerPersonPrice = !!perPersonPrice
  const burnResult = useOfferPageBurnPoints(
    price.primaryPrice.total,
    loyaltyProductType ?? 'cruise_group_3',
    undefined,
    { disabled: !loyaltyProductType || !price.hasPrice || !!promotionalPrice },
  )
  const showBurnPrice = price.memberPriceType !== 'STANDARD_PRICE' && burnResult.enabled && burnResult.priceInPoints !== undefined
  const openCabinInfoModal = useCallback(() => {
    showModal(<Modal title={cabinTypeLabels[price.category] ?? price.category}>
      <BodyText variant="medium">
        {cabinDescriptions[price.category]}
      </BodyText>
    </Modal>)
  }, [cabinDescriptions, cabinTypeLabels, price.category, showModal])

  return <PriceItemWrapper
    direction="horizontal"
    tabletDirection="vertical"
    horizontalAlign="space-between"
    verticalAlign="center"
    gap={8}
  >
    <CabinPriceCaption
      onInfoIconClick={openCabinInfoModal}
      $hideIconOnDesktop={hideIconOnDesktop}
    >
      {price.hasPrice ? (
        <CruisePriceFromLabel
          cabinType={categoryLabel}
          isBundle={price.isBundle}
        />
      ) : (
        categoryLabel
      )}
    </CabinPriceCaption>

    <Group direction="vertical" gap={4} horizontalAlign="end" tabletHorizontalAlign="start">
      {price.hasPrice && showBurnPrice && <OfferPageBurnPriceDisplay
        totalPrice={price.primaryPrice.total}
        pointsToApply={burnResult.pointsToBurn}
        priceInPoints={burnResult.priceInPoints}
        memberPrice={price.memberPrice}
        size="S"
        stacked
      />}

      {promotionalPrice && <Group direction="vertical" gap={4} horizontalAlign="end" tabletHorizontalAlign="start">
        {hasPerPersonPrice ? <Caption variant="medium" colour="success-dark">
          <FormattedMessage
            id="01ZsUhS60i_gK_k4VanJ"
            defaultMessage="{price}/person"
            description="Primary per-person price displayed above the total family cruise promotional price"
            values={{
              price: <Heading as="span" variant="heading6" colour="success-dark">
                <FormatCurrency value={perPersonPrice} />
              </Heading>,
            }}
          />
        </Caption> : <LuxPlusPriceStack size="S" price={promotionalPrice.total} saleUnit={promotionalPrice.type} />}
        {!!promotionalPrice.perPersonPrice && <Caption variant="medium" colour="light-foreground-alt">
          <FormatCurrency value={promotionalPrice.total} />&nbsp;
          <CruisePromotionPriceUnit promotion={promotionalPrice.promotion} />
        </Caption>}
      </Group>}

      {!promotionalPrice && !!price.primaryPrice.total && !showBurnPrice && <Group
        direction="horizontal"
        verticalAlign="center"
        gap={4}
      >
        {price.isBundle ? <>
          <Heading as="span" variant="heading6" colour="success-dark">
            <FormatCurrency value={price.primaryPrice.total} />
          </Heading>
          <Caption variant="medium" colour="light-foreground-alt" as="span">
            {intl.formatMessage({
              id: 'oScuwLDRHT-zAp6w64wC',
              defaultMessage: '/{priceType}',
              description: 'Per-unit price type label for cruise secondary price (e.g. /person)',
            }, { priceType: price.primaryPrice.type })}
          </Caption>
        </> : <LuxPlusPriceStack
          size="S"
          price={price.primaryPrice.total}
          memberPrice={price.memberPrice}
          saleUnit={price.primaryPrice.type}
          mode={price.memberPriceType === 'STANDARD_PRICE' ? 'member-only' : undefined}
        />}
      </Group>}

      {price.secondaryPrice && !showBurnPrice && <Group direction="horizontal" gap={4}>
        <Caption variant="large" colour="light-foreground-alt" as="span">
          <FormatCurrency value={price.secondaryPrice.total} />
        </Caption>
        <Caption variant="large" colour="light-foreground-alt" as="span">
          {intl.formatMessage({
            id: 'oScuwLDRHT-zAp6w64wC',
            defaultMessage: '/{priceType}',
            description: 'Per-unit price type label for cruise secondary price (e.g. /person)',
          }, { priceType: price.secondaryPrice.type })}
        </Caption>
      </Group>}

      {!price.hasPrice && <Group direction="vertical" gap={4} tabletHorizontalAlign="start" horizontalAlign="end">
        <Heading variant="heading5" colour="neutral-four">
          <FormattedMessage
            id="yMfXxKPttoB6lFemIeuH"
            defaultMessage="Sold out"
            description="Label indicating cabin category is sold out in cabin selection"
          />
        </Heading>
        {renderSoldOutContent?.(price.category)}
      </Group>}

      {!!price.discountPercentage && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={price.discountPercentage}
      />}

      {price.hasPrice && !promotionalPrice && <Caption variant="medium" colour="light-foreground-alt">
        <FormattedMessage
          id="5Tekcjnyh3bWzSGZIiez"
          defaultMessage="Twin Share"
          description="Label indicating price is for twin share accommodation"
        />
      </Caption>}
    </Group>
  </PriceItemWrapper>
}

export default CruiseDepartureCabinPriceItem
